import React, { useEffect, useState, useRef } from 'react';
import { Trash2Icon, CheckIcon } from 'lucide-react';
type Props = {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  active: boolean;
};
export function SignaturePad({
  value,
  onChange,
  active
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const lastPoint = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const [hasDrawn, setHasDrawn] = useState(Boolean(value));
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#e8e8ee';
    ctx.lineWidth = 2.2;
  }, []);
  const getPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };
  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawing.current = true;
    lastPoint.current = getPoint(e);
    canvasRef.current?.setPointerCapture(e.pointerId);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !lastPoint.current) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    const p = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPoint.current = p;
    if (!hasDrawn) setHasDrawn(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    lastPoint.current = null;
    const url = canvasRef.current?.toDataURL('image/png') || null;
    onChange(url);
  };
  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onChange(null);
  };
  return <div className="space-y-2" data-id="element-95">
      <div className={`relative rounded-lg border bg-[#0a0a0e] transition-all ${active ? 'border-violet-400/60 shadow-[0_0_0_3px_rgba(167,139,250,0.12)]' : 'border-white/8'}`} data-id="element-96">
        <canvas ref={canvasRef} className="sig-canvas w-full h-32 rounded-lg" onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} data-id="element-97" />
        {/* Baseline */}
        <div className="absolute left-4 right-4 bottom-5 border-b border-dashed border-white/10 pointer-events-none" data-id="element-98" />
        {/* Placeholder */}
        {!hasDrawn && <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-99">
            <span className="text-[11px] text-zinc-600 italic" data-id="element-100">
              Draw your signature here
            </span>
          </div>}
        {/* X mark */}
        <div className="absolute left-4 bottom-4 text-zinc-700 text-xs font-mono pointer-events-none" data-id="element-101">
          ×
        </div>
      </div>
      <div className="flex items-center justify-between text-[11px]" data-id="element-102">
        <button onClick={clear} className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 transition-colors" data-id="element-103">
          <Trash2Icon className="w-3 h-3" data-id="element-104" />
          Clear
        </button>
        {hasDrawn && <span className="flex items-center gap-1 text-emerald-300/90 font-mono" data-id="element-105">
            <CheckIcon className="w-3 h-3" data-id="element-106" />
            Captured
          </span>}
      </div>
    </div>;
}